import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { PageHead, Notice } from "../components/ui";
import Back from "../components/Back";

/**
 * Everything that isn't playing golf.
 *
 * One page rather than a scattering: the society's own settings, your
 * account, which society you're in, and the site-wide view if you run it.
 * Each row goes somewhere specific — this is a map, not a dumping ground.
 */

const Row = ({ to, onClick, title, sub, badge }) => {
  const inner = (
    <span>
      <strong>
        {title}
        {badge && <span className="pill pill-live" style={{ marginLeft: ".45rem" }}>{badge}</span>}
      </strong>
      <small>{sub}</small>
    </span>
  );
  return to ? <Link to={to} className="menu-row">{inner}</Link>
            : <button className="menu-row" onClick={onClick}>{inner}</button>;
};

function Group({ title, children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h2 className="eyebrow" style={{ marginBottom: ".5rem" }}>{title}</h2>
      <div className="menu">{items}</div>
    </div>
  );
}

/**
 * Configuring the society.
 *
 * Members and courses sit here rather than behind the avatar: they're
 * society content, not personal preferences, and an avatar conventionally
 * means "me" — nobody looks there for a list of other people.
 *
 * Organisers and admins only. A player has nothing to do on this page, so
 * they never see the cog that leads to it.
 */
export default function Settings() {
  const { society, profile, isAdmin, isOrganiser, isPlatformAdmin } = useSession();

  return (
    <>
      <Back to="/">Back</Back>
      <PageHead title={society?.name} sub="Society settings" />

      {!isOrganiser ? (
        <Notice kind="error">Only an organiser can change the society.</Notice>
      ) : (
      <>
      <Group title="People and places">
        <Row to="/members" title="Members"
             sub="The roster, handicap indexes, invitations" />
        <Row to="/courses" title="Courses"
             sub="Par and stroke index for each set of tees" />
        <Row to="/competitions" title="Competitions"
             sub="Every competition, finished ones included" />
      </Group>

      <Group title="The society itself">
        {isAdmin && (
          <Row to="/society-settings" title="Name, code and sign-ups"
               sub={`Rename ${society?.name}, change the join code, who may create an account`} />
        )}
        {!isAdmin && (
          <div className="menu-row" style={{ cursor: "default" }}>
            <span>
              <strong>Name, code and sign-ups</strong>
              <small>Admins only.</small>
            </span>
          </div>
        )}
        {isPlatformAdmin && (
          <Row to="/platform" title="Site administration" badge="platform"
               sub="Every society on PostcardGolf" />
        )}
      </Group>

      <p className="muted" style={{ fontSize: ".75rem", lineHeight: 1.6 }}>
        Anything you're not allowed to change isn't shown. The database
        enforces the same rules, so nothing here is only hidden.
      </p>
      </>
      )}
    </>
  );
}
